import {
  BookingStatus,
  enumBookingStatus,
  enumLoadStatus,
  enumPurchaseLoadStatus,
  enumPurchaseOrderStatus,
  enumSalesOrderStatus,
  LoadStatus,
  PurchaseLoadStatus,
  PurchaseOrderStatus,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { ThemeColor } from '@rematter/ui-web/types';

import { DefaultTranslations } from '../i18n/useTranslations';

export const getBookingStatusColor = (status?: BookingStatus): ThemeColor => {
  switch (status) {
    case enumBookingStatus.Draft:
      return 'neutral';
    case enumBookingStatus.Open:
      return 'primary';
    case enumBookingStatus.Shipped:
      return 'warning';
    case enumBookingStatus.Completed:
      return 'success';
    case enumBookingStatus.Voided:
      return 'failure';
    default:
      return 'neutral';
  }
};

type BookingStatusTextArgs = {
  status?: BookingStatus;
  t: DefaultTranslations;
};

export const getBookingStatusText = ({ status, t }: BookingStatusTextArgs) => {
  switch (status) {
    case enumBookingStatus.Draft:
      return t.bookingDetail.status.draft;
    case enumBookingStatus.Open:
      return t.bookingDetail.status.open;
    case enumBookingStatus.Shipped:
      return t.bookingDetail.status.shipped;
    case enumBookingStatus.Completed:
      return t.bookingDetail.status.completed;
    case enumBookingStatus.Voided:
      return t.bookingDetail.status.voided;
    default:
      return status;
  }
};

export const loadStatusTextMap: Record<
  LoadStatus,
  (t: DefaultTranslations, isReviewed?: boolean) => string
> = {
  [enumLoadStatus.Unassigned]: (t) => t.loadDetail.status.unassigned,
  [enumLoadStatus.Pending]: (t) => t.loadDetail.status.pending,
  [enumLoadStatus.ContainerAtPort]: (t) => t.loadDetail.status.containerAtPort,
  [enumLoadStatus.ContainerInTransit]: (t) =>
    t.loadDetail.status.containerInTransit,
  [enumLoadStatus.ContainerAtFacility]: (t) =>
    t.loadDetail.status.containerAtFacility,
  [enumLoadStatus.ContainerLoaded]: (t) => t.loadDetail.status.containerLoaded,
  [enumLoadStatus.ContainerReturning]: (t) =>
    t.loadDetail.status.containerReturning,
  [enumLoadStatus.ContainerReadyToShip]: (t) =>
    t.loadDetail.status.containerReadyToShip,
  [enumLoadStatus.Shipped]: (t) => t.loadDetail.status.shipped,
  [enumLoadStatus.Received]: (t, isReviewed) =>
    isReviewed
      ? t.loadDetail.status.pendingReconciliation
      : t.loadDetail.status.received,
  [enumLoadStatus.Reconciled]: (t) => t.loadDetail.status.reconciled,
  [enumLoadStatus.Voided]: (t) => t.loadDetail.status.voided,
};

export const loadStatusColors: Record<LoadStatus, string> = {
  [enumLoadStatus.Unassigned]: 'bg-neutral/40',
  [enumLoadStatus.Pending]: 'bg-primary',
  [enumLoadStatus.ContainerAtPort]: 'bg-info',
  [enumLoadStatus.ContainerInTransit]: 'bg-info',
  [enumLoadStatus.ContainerAtFacility]: 'bg-warning',
  [enumLoadStatus.ContainerLoaded]: 'bg-warning',
  [enumLoadStatus.ContainerReturning]: 'bg-info',
  [enumLoadStatus.ContainerReadyToShip]: 'bg-info',
  [enumLoadStatus.Shipped]: 'bg-primary',
  [enumLoadStatus.Received]: 'bg-warning',
  [enumLoadStatus.Reconciled]: 'bg-success',
  [enumLoadStatus.Voided]: 'bg-failure',
};

export const loadStatusThemeColors: Record<LoadStatus, ThemeColor> = {
  [enumLoadStatus.Unassigned]: 'neutral',
  [enumLoadStatus.Pending]: 'primary',
  [enumLoadStatus.ContainerAtPort]: 'neutral',
  [enumLoadStatus.ContainerInTransit]: 'neutral',
  [enumLoadStatus.ContainerAtFacility]: 'warning',
  [enumLoadStatus.ContainerLoaded]: 'warning',
  [enumLoadStatus.ContainerReturning]: 'neutral',
  [enumLoadStatus.ContainerReadyToShip]: 'neutral',
  [enumLoadStatus.Shipped]: 'primary',
  [enumLoadStatus.Received]: 'warning',
  [enumLoadStatus.Reconciled]: 'success',
  [enumLoadStatus.Voided]: 'failure',
};

export const getSalesOrderStatusColor = (
  status?: SalesOrderStatus,
): ThemeColor => {
  switch (status) {
    case enumSalesOrderStatus.Draft:
      return 'neutral';
    case enumSalesOrderStatus.Open:
      return 'primary';
    case enumSalesOrderStatus.Finished:
      return 'success';
    case enumSalesOrderStatus.Voided:
      return 'failure';
    default:
      return 'neutral';
  }
};

type SalesOrderStatusTextArgs = {
  status?: SalesOrderStatus;
  t: DefaultTranslations;
};

export const getSalesOrderStatusText = ({
  status,
  t,
}: SalesOrderStatusTextArgs) => {
  switch (status) {
    case enumSalesOrderStatus.Draft:
      return t.salesOrderDetail.status.draft;
    case enumSalesOrderStatus.Open:
      return t.salesOrderDetail.status.open;
    case enumSalesOrderStatus.Finished:
      return t.salesOrderDetail.status.finished;
    case enumSalesOrderStatus.Voided:
      return t.salesOrderDetail.status.voided;
    default:
      return status;
  }
};

export const getPurchaseOrderStatusColor = (
  status?: PurchaseOrderStatus,
): ThemeColor => {
  switch (status) {
    case enumPurchaseOrderStatus.Draft:
      return 'neutral';
    case enumPurchaseOrderStatus.Open:
      return 'primary';
    case enumPurchaseOrderStatus.Closed:
      return 'success';
    case enumPurchaseOrderStatus.Voided:
      return 'failure';
    default:
      return 'neutral';
  }
};

type PurchaseOrderStatusTextArgs = {
  status?: PurchaseOrderStatus;
  t: DefaultTranslations;
};

export const getPurchaseOrderStatusText = ({
  status,
  t,
}: PurchaseOrderStatusTextArgs) => {
  switch (status) {
    case enumPurchaseOrderStatus.Draft:
      return t.purchaseOrderDetail.status.draft;
    case enumPurchaseOrderStatus.Open:
      return t.purchaseOrderDetail.status.open;
    case enumPurchaseOrderStatus.Closed:
      return t.purchaseOrderDetail.status.closed;
    case enumPurchaseOrderStatus.Voided:
      return t.purchaseOrderDetail.status.voided;
    default:
      return status;
  }
};

export const getPurchaseLoadStatusColor = (
  status?: PurchaseLoadStatus,
): ThemeColor => {
  switch (status) {
    case enumPurchaseLoadStatus.Open:
      return 'primary';
    case enumPurchaseLoadStatus.Held:
      return 'warning';
    case enumPurchaseLoadStatus.InventoryComplete:
    case enumPurchaseLoadStatus.PriceComplete:
      return 'teal';
    case enumPurchaseLoadStatus.Paid:
      return 'success';
    case enumPurchaseLoadStatus.Voided:
      return 'failure';
    default:
      return 'neutral';
  }
};

type PurchaseLoadStatusTextArgs = {
  status?: PurchaseLoadStatus;
  t: DefaultTranslations;
};

export const getPurchaseLoadStatusText = ({
  status,
  t,
}: PurchaseLoadStatusTextArgs) => {
  switch (status) {
    case enumPurchaseLoadStatus.Open:
      return t.purchaseLoadsView.status.open;
    case enumPurchaseLoadStatus.Held:
      return t.purchaseLoadsView.status.held;
    case enumPurchaseLoadStatus.InventoryComplete:
      return t.purchaseLoadsView.status.inventoryComplete;
    case enumPurchaseLoadStatus.PriceComplete:
      return t.purchaseLoadsView.status.priceComplete;
    case enumPurchaseLoadStatus.Paid:
      return t.purchaseLoadsView.status.paid;
    case enumPurchaseLoadStatus.Voided:
      return t.purchaseLoadsView.status.voided;
    default:
      return status;
  }
};

export const purchaseLoadStatusColors: Record<PurchaseLoadStatus, string> = {
  [enumPurchaseLoadStatus.Open]: 'bg-primary',
  [enumPurchaseLoadStatus.Held]: 'bg-warning',
  [enumPurchaseLoadStatus.InventoryComplete]: 'bg-teal',
  [enumPurchaseLoadStatus.PriceComplete]: 'bg-teal',
  [enumPurchaseLoadStatus.Paid]: 'bg-success',
  [enumPurchaseLoadStatus.Voided]: 'bg-failure',
};

export const purchaseLoadStatusThemeColors: Record<
  PurchaseLoadStatus,
  ThemeColor
> = {
  [enumPurchaseLoadStatus.Open]: 'primary',
  [enumPurchaseLoadStatus.Held]: 'warning',
  [enumPurchaseLoadStatus.InventoryComplete]: 'teal',
  [enumPurchaseLoadStatus.PriceComplete]: 'teal',
  [enumPurchaseLoadStatus.Paid]: 'success',
  [enumPurchaseLoadStatus.Voided]: 'failure',
};

export const purchaseLoadStatusTextMap: Record<
  PurchaseLoadStatus,
  (t: DefaultTranslations) => string
> = {
  [enumPurchaseLoadStatus.Open]: (t) => t.purchaseLoadsView.status.open,
  [enumPurchaseLoadStatus.Held]: (t) => t.purchaseLoadsView.status.held,
  [enumPurchaseLoadStatus.InventoryComplete]: (t) =>
    t.purchaseLoadsView.status.inventoryComplete,
  [enumPurchaseLoadStatus.PriceComplete]: (t) =>
    t.purchaseLoadsView.status.priceComplete,
  [enumPurchaseLoadStatus.Paid]: (t) => t.purchaseLoadsView.status.paid,
  [enumPurchaseLoadStatus.Voided]: (t) => t.purchaseLoadsView.status.voided,
};

export const LOAD_STATUS_ORDER: Record<string, number> = {
  [enumLoadStatus.Unassigned]: 0,
  [enumLoadStatus.Pending]: 1,
  [enumLoadStatus.ContainerReadyToShip]: 2,
  [enumLoadStatus.Shipped]: 3,
  [enumLoadStatus.Received]: 4,
  [enumLoadStatus.Reconciled]: 5,
};

export const getLoadRevertTargets = (status: LoadStatus): LoadStatus[] => {
  const currentOrder = LOAD_STATUS_ORDER[status] ?? -1;
  if (currentOrder <= 0 || status === enumLoadStatus.Voided) return [];
  return Object.entries(LOAD_STATUS_ORDER)
    .filter(([, order]) => order < currentOrder)
    .sort(([, a], [, b]) => b - a)
    .map(([targetStatus]) => targetStatus as LoadStatus);
};

export const canVoidLoad = (status: LoadStatus): boolean =>
  status !== enumLoadStatus.Voided && status !== enumLoadStatus.Reconciled;
